@file:Suppress("DEPRECATION")

package com.schoolconnect.app.designsystem.list

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsHoveredAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.foundation.hoverable
import androidx.compose.ui.graphics.RectangleShape
import androidx.compose.foundation.layout.defaultMinSize
import androidx.compose.ui.draw.drawBehind
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.combinedClickable
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Check
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material.icons.filled.Info
import androidx.compose.material.icons.filled.Notifications
import androidx.compose.material3.FilledTonalIconButton
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.IconButtonDefaults
import androidx.compose.material3.LocalContentColor
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalClipboardManager
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp

/** Adds the standard list shortcut for opening an item's existing view/details surface. */
fun Modifier.onViewDoubleClick(onView: (() -> Unit)?): Modifier =
    if (onView == null) {
        this
    } else {
        combinedClickable(
            onClick = {},
            onDoubleClick = onView
        )
    }

@Composable
fun ListTableHeaderRow(
    columns: List<ListTableHeaderColumn>,
    modifier: Modifier = Modifier
) {
    Row(
        modifier = modifier
            .fillMaxWidth()
            .heightIn(min = 44.dp)
            .background(AdminListDefaults.HeaderBackground),
        verticalAlignment = Alignment.CenterVertically
    ) {
        columns.forEach { column ->
            TextButton(
                onClick = column.onClick,
                modifier = Modifier
                    .defaultMinSize(minWidth = 0.dp, minHeight = 0.dp)
                    .heightIn(min = 40.dp, max = 44.dp)
                    .weight(column.weight)
                    .padding(horizontal = 4.dp)
            ) {
                AdminListSortLabel(
                    text = column.label,
                    selected = column.selected,
                    ascending = column.ascending,
                    modifier = Modifier.fillMaxWidth()
                )
            }
        }
    }
}

data class ListTableHeaderColumn(
    val label: String,
    val weight: Float,
    val selected: Boolean,
    val ascending: Boolean,
    val onClick: () -> Unit
)

@Composable
fun ListTableRowContainer(
    modifier: Modifier = Modifier,
    containerColor: Color = Color.White,
    selected: Boolean = false,
    content: @Composable RowScope.() -> Unit
) {
    val interactionSource = remember { MutableInteractionSource() }
    val hovered by interactionSource.collectIsHoveredAsState()
    Surface(
        modifier = modifier.hoverable(interactionSource).drawBehind {
            drawLine(
                color = AdminListDefaults.Border,
                start = androidx.compose.ui.geometry.Offset(0f, size.height),
                end = androidx.compose.ui.geometry.Offset(size.width, size.height),
                strokeWidth = 1.dp.toPx()
            )
        },
        shape = RectangleShape,
        tonalElevation = 0.dp,
        shadowElevation = 0.dp,
        color = when {
            selected -> AdminListDefaults.SelectedRow
            hovered -> AdminListDefaults.HoverRow
            else -> containerColor
        }
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .heightIn(min = 52.dp)
                .padding(horizontal = 16.dp, vertical = 4.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            content()
        }
    }
}

@Composable
fun RowScope.ListTableCell(
    text: String,
    cellWeight: Float,
    modifier: Modifier = Modifier
) {
    Box(
        modifier = modifier
            .weight(cellWeight)
            .padding(horizontal = 6.dp)
    ) {
        AdminListTableBodyText(
            text = text,
            modifier = Modifier.fillMaxWidth(),
            color = AdminListDefaults.Navy
        )
    }
}

@Composable
fun RowScope.ListTableRefIdCell(
    text: String,
    cellWeight: Float,
    modifier: Modifier = Modifier
) {
    val clipboardManager = LocalClipboardManager.current
    Row(
        modifier = modifier
            .weight(cellWeight)
            .padding(horizontal = 3.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.Start
    ) {
        Box(modifier = Modifier.weight(1f, fill = false)) {
            SelectionContainer {
                Text(
                    text = text,
                    style = MaterialTheme.typography.bodyMedium,
                    fontFamily = FontFamily.Monospace,
                    fontWeight = FontWeight.SemiBold,
                    letterSpacing = (-0.1).sp,
                    maxLines = 1,
                    softWrap = false,
                    overflow = TextOverflow.Ellipsis
                )
            }
        }
        IconButton(
            onClick = { clipboardManager.setText(AnnotatedString(text)) },
            modifier = Modifier
                .padding(start = 4.dp)
                .size(24.dp),
            colors = IconButtonDefaults.iconButtonColors(
                contentColor = MaterialTheme.colorScheme.primary
            )
        ) {
            CopyGlyphIcon()
        }
    }
}

@Composable
private fun CopyGlyphIcon(
    modifier: Modifier = Modifier
) {
    val color = LocalContentColor.current
    Box(
        modifier = modifier
            .size(14.dp)
    ) {
        Box(
            modifier = Modifier
                .align(Alignment.TopStart)
                .size(9.dp)
                .border(1.4.dp, color, androidx.compose.foundation.shape.RoundedCornerShape(1.5.dp))
        )
        Box(
            modifier = Modifier
                .align(Alignment.TopStart)
                .offset(x = 3.dp, y = 3.dp)
                .size(9.dp)
                .border(1.4.dp, color, androidx.compose.foundation.shape.RoundedCornerShape(1.5.dp))
        )
    }
}

@Composable
fun RowScope.ListTableActionButtons(
    active: Boolean,
    onEdit: () -> Unit,
    onToggleStatus: () -> Unit,
    onDelete: () -> Unit,
    onDetails: (() -> Unit)? = null,
    onNotify: (() -> Unit)? = null,
    actionsWeight: Float,
    modifier: Modifier = Modifier
) {
    Row(
        modifier = modifier
            .weight(actionsWeight)
            .padding(start = 8.dp),
        horizontalArrangement = Arrangement.spacedBy(8.dp, Alignment.End),
        verticalAlignment = Alignment.CenterVertically
    ) {
        FilledTonalIconButton(
            onClick = onEdit,
            colors = IconButtonDefaults.filledTonalIconButtonColors(
                containerColor = MaterialTheme.colorScheme.primaryContainer,
                contentColor = MaterialTheme.colorScheme.onPrimaryContainer
            )
        ) {
            Icon(imageVector = Icons.Default.Edit, contentDescription = "Edit")
        }
        if (onDetails != null) {
            FilledTonalIconButton(
                onClick = onDetails,
                colors = IconButtonDefaults.filledTonalIconButtonColors(
                    containerColor = MaterialTheme.colorScheme.secondaryContainer,
                    contentColor = MaterialTheme.colorScheme.onSecondaryContainer
                )
            ) {
                Icon(imageVector = Icons.Default.Info, contentDescription = "Details")
            }
        }
        if (onNotify != null) {
            FilledTonalIconButton(
                onClick = onNotify,
                colors = IconButtonDefaults.filledTonalIconButtonColors(
                    containerColor = MaterialTheme.colorScheme.primaryContainer,
                    contentColor = MaterialTheme.colorScheme.onPrimaryContainer
                )
            ) {
                Icon(imageVector = Icons.Default.Notifications, contentDescription = "Notify")
            }
        }
        FilledTonalIconButton(
            onClick = onToggleStatus,
            colors = IconButtonDefaults.filledTonalIconButtonColors(
                containerColor = if (active) MaterialTheme.colorScheme.surfaceVariant else MaterialTheme.colorScheme.tertiaryContainer,
                contentColor = if (active) MaterialTheme.colorScheme.onSurfaceVariant else MaterialTheme.colorScheme.onTertiaryContainer
            )
        ) {
            Icon(
                imageVector = if (active) Icons.Default.Close else Icons.Default.Check,
                contentDescription = if (active) "Inactivate" else "Activate"
            )
        }
        FilledTonalIconButton(
            onClick = onDelete,
            colors = IconButtonDefaults.filledTonalIconButtonColors(
                containerColor = MaterialTheme.colorScheme.errorContainer,
                contentColor = MaterialTheme.colorScheme.onErrorContainer
            )
        ) {
            Icon(imageVector = Icons.Default.Delete, contentDescription = "Delete")
        }
    }
}
